/**
 * Which links a person sees, decided by the permission matrix.
 *
 * Extracted from the layout so it can be tested. It was inline, and a mutation
 * that removed the filter entirely changed nothing that any test could see
 * (R-120): the layout is a React component and nothing here renders one, so the
 * only honest options were to add a rendering test or make the rule a function.
 *
 * `module: null` means everybody internal: the dashboard, and the permissions
 * screen itself, which FR-809 makes readable by anyone because a person is
 * allowed to know what they may do.
 *
 * **Hiding a link is a courtesy.** Every route re-checks, and so does the
 * database. This decides what is worth showing, never what is allowed.
 */
export interface NavItem<Icon = unknown> {
  href: string;
  label: string;
  icon: Icon;
  count?: number;
  /** The module whose `view` permission reveals this, or null for everybody. */
  module: string | null;
  /**
   * Other modules the screen READS before it can render. R-175.
   *
   * The job board reads jobs AND machines in one batch, and `/api/data` refuses
   * the whole batch if any call is short a permission. So a person with
   * `machines.view` and not `jobs.view` was offered the Machines screen and
   * then told they lack `jobs.view` on arrival.
   *
   * No role has one without the other today. **The matrix is the client's to
   * edit**, and this is the same "one checkbox away" as R-174.
   */
  alsoNeeds?: readonly string[];
}

/**
 * A named run of screens in the sidebar.
 *
 * Twenty items in one flat list is a list nobody reads: Katherine hunts for
 * "Waste" between "Stocktakes" and "Recipes" every time. Grouping is not
 * decoration, it is how somebody finds the third-most-used screen without
 * reading all twenty. R-214.
 */
export interface NavGroup<T> {
  /** Shown as a mono micro-label, which is one of the two places uppercase survives. */
  name: string;
  items: NavItem<T>[];
}

/**
 * Group the visible items, and DROP AN EMPTY GROUP.
 *
 * A heading with nothing under it tells somebody a section exists that they
 * cannot reach, which is the same failure as a link to a screen they may not
 * open (R-175). A staff member who cannot see purchasing should not learn that
 * purchasing exists from a heading.
 */
export function groupNav<T>(
  groups: readonly NavGroup<T>[],
  can: Record<string, string[]>,
): NavGroup<T>[] {
  return groups
    .map((g) => ({ ...g, items: visibleNav(g.items, can) }))
    .filter((g) => g.items.length > 0);
}

export function visibleNav<T>(
  items: readonly NavItem<T>[],
  can: Record<string, string[]>,
): NavItem<T>[] {
  const mayView = (module: string) => (can[module] ?? []).includes('view');

  return items.filter((item) => {
    if (item.module !== null && !mayView(item.module)) return false;
    // Every module the screen reads, not only the one it is filed under. A link
    // that leads to "you do not have jobs.view permission" is worse than no
    // link: the person was told they could.
    return (item.alsoNeeds ?? []).every(mayView);
  });
}
